import type { CSSProperties, FocusEvent, ReactNode, RefObject } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

type LoginPasswordStepProps = {
  passwordInputRef: RefObject<HTMLInputElement | null>;
  password: string;
  onPasswordChange: (value: string) => void;
  containerStyle?: CSSProperties;
  showPassword: boolean;
  onToggleShowPassword: () => void;
  capsLockOn: boolean;
  onCapsLockChange: (value: boolean) => void;
  isAssuranceChecking: boolean;
  loginStepErrorCard: ReactNode;
  showLoginStepErrorCard: boolean;
  showLegacyInlineLoginError: boolean;
  error: string;
  overlayInputStyle: CSSProperties;
  loginFieldShellStyle: CSSProperties;
  onInputFocus: (e: FocusEvent<HTMLInputElement>) => void;
  onInputBlur: (e: FocusEvent<HTMLInputElement>) => void;
  onRecoverPassword: () => void;
};

export function LoginPasswordStep({
  passwordInputRef,
  password,
  onPasswordChange,
  containerStyle,
  showPassword,
  onToggleShowPassword,
  capsLockOn,
  onCapsLockChange,
  isAssuranceChecking,
  loginStepErrorCard,
  showLoginStepErrorCard,
  showLegacyInlineLoginError,
  error,
  overlayInputStyle,
  loginFieldShellStyle,
  onInputFocus,
  onInputBlur,
  onRecoverPassword,
}: LoginPasswordStepProps) {
  return (
    <div style={{ position: 'relative', margin: 0, ...containerStyle }}>
      {loginStepErrorCard}
      <div style={loginFieldShellStyle} aria-hidden={showLoginStepErrorCard}>
        <div style={{ position: 'relative' }}>
          <input
            ref={passwordInputRef}
            data-testid="login-password-input"
            type={showPassword ? 'text' : 'password'}
            id="current-password"
            placeholder="Digite sua senha"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            autoFocus
            tabIndex={showLoginStepErrorCard ? -1 : undefined}
            onChange={(e) => {
              e.currentTarget.setCustomValidity('');
              onPasswordChange(e.target.value);
            }}
            onKeyUp={(e) => onCapsLockChange(e.getModifierState('CapsLock'))}
            onKeyDown={(e) => onCapsLockChange(e.getModifierState('CapsLock'))}
            style={{
              ...overlayInputStyle,
              paddingRight: 54,
            }}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
          <button
            type="button"
            data-testid="login-toggle-password"
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            onClick={onToggleShowPassword}
            style={{
              position: 'absolute',
              right: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 34,
              height: 34,
              border: 'none',
              background: 'transparent',
              color: '#64748b',
              cursor: 'pointer',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        {isAssuranceChecking && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 10,
            color: '#475569',
            fontSize: '0.82rem',
            fontWeight: 600,
          }}>
            <Loader2 size={15} style={{ animation: 'lk-spin 1s linear infinite' }} />
            Conferindo recursos de segurança desta conta...
          </div>
        )}
        {password.length > 0 && password.length < 8 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            <span style={{ color: '#fecaca', fontSize: '0.78rem', fontWeight: 500 }}>A senha deve ter pelo menos 8 caracteres.</span>
          </div>
        )}
        {capsLockOn && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l7 10H5l7-10z" /><rect x="9" y="14" width="6" height="6" rx="1" /></svg>
            <span style={{ color: '#d97706', fontSize: '0.78rem', fontWeight: 500 }}>Caps Lock ativado — letras em maiúsculo</span>
          </div>
        )}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          marginTop: capsLockOn ? 8 : 12,
        }}>
          <button
            type="button"
            data-testid="login-recover-password"
            onClick={onRecoverPassword}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#a78bfa',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 500,
              padding: '0 0 0 10px',
              opacity: 0.6,
              whiteSpace: 'nowrap',
              textShadow: '0 0 14px rgba(196,181,253,0.42)',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
              e.currentTarget.style.opacity = '0.6';
            }}
          >
            Esqueci a senha
          </button>
        </div>
      </div>
      {showLegacyInlineLoginError && !showLoginStepErrorCard && error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 12,
          padding: '10px 12px',
          borderRadius: 12,
          background: 'rgba(239, 68, 68, 0.10)',
          color: '#fecaca',
          border: '1px solid rgba(239, 68, 68, 0.22)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" /></svg>
          {error}
        </div>
      )}
    </div>
  );
}
